/**
 * Chat hook
 *
 * Handles sending messages and streaming responses.
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../stores/userStore';
import { sendMessage, confirmAction, submitFeedback, getSessionFeedback } from '../api/chat';
import type { ActionPreview, Message } from '../types';

export function useChat() {
  const { t } = useTranslation();
  const {
    currentSessionUuid,
    messages,
    isSending,
    isStreaming,
    toolStatus,
    addMessage,
    updateLastMessage,
    setSending,
    setStreaming,
    setToolStatus,
    updateSessionTitle,
    moveSessionToTop,
  } = useUserStore();

  const [actionPreview, setActionPreview] = useState<ActionPreview | null>(null);
  const [feedback, setFeedback] = useState<Record<number, 'up' | 'down'>>({});
  const abortRef = useRef<(() => void) | null>(null);

  // Load feedback when session changes
  useEffect(() => {
    if (currentSessionUuid) {
      getSessionFeedback(currentSessionUuid)
        .then((data) => {
          setFeedback(data.feedback || {});
        })
        .catch((err) => {
          console.error('Failed to load feedback:', err);
        });
    } else {
      setFeedback({});
    }
  }, [currentSessionUuid]);

  /**
   * Send a message and handle streaming response
   */
  const send = useCallback(
    async (content: string) => {
      if (!currentSessionUuid || !content.trim() || isSending) {
        return;
      }

      setSending(true);
      setStreaming(true);
      setToolStatus('Razmišljam...');  // Default status while AI is processing
      setActionPreview(null);

      // Add user message
      const userMessage: Message = {
        role: 'user',
        content: content.trim(),
        created_at: new Date().toISOString(),
      };
      addMessage(userMessage);

      // Add empty assistant message for streaming
      const assistantMessage: Message = {
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
      };
      addMessage(assistantMessage);

      // Stream response
      abortRef.current = sendMessage(currentSessionUuid, content.trim(), {
        onContent: (text) => {
          updateLastMessage(text);
        },
        onToolStart: (toolName, _toolId) => {
          // Map tool names to user-friendly Serbian labels
          const toolLabels: Record<string, string> = {
            calculate_natal_chart: 'Izracunavam natalnu kartu...',
            calculate_transits: 'Analiziram transite...',
            calculate_synastry: 'Analiziram sinastrijsku kompatibilnost...',
            calculate_solar_return: 'Izracunavam solarni povratak...',
            compare_solar_return_locations: 'Uporedjujem lokacije za solar...',
            get_current_transits: 'Proveravam trenutne transite...',
            sacuvaj_osobu: 'Cuvam podatke...',
          };
          setToolStatus(toolLabels[toolName] || 'Obradjujem...');
        },
        onToolResult: (_toolId, _result) => {
          // Could display tool results in the future
        },
        onActionPreview: (preview) => {
          setActionPreview(preview);
        },
        onDone: (title) => {
          setSending(false);
          setStreaming(false);
          setToolStatus(null);
          if (currentSessionUuid) {
            moveSessionToTop(currentSessionUuid);
            if (title) {
              updateSessionTitle(currentSessionUuid, title);
            }
          }
        },
        onError: (error) => {
          console.error('Chat error:', error);
          setSending(false);
          setStreaming(false);
          setToolStatus(null);
          // Update assistant message with error
          updateLastMessage(`\n\n❌ ${t('errors.chatError')}: ${error}`);
        },
      });
    },
    [
      currentSessionUuid,
      isSending,
      setSending,
      setStreaming,
      addMessage,
      updateLastMessage,
      updateSessionTitle,
      moveSessionToTop,
    ]
  );

  /**
   * Abort current streaming request
   */
  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
      setSending(false);
      setStreaming(false);
      setToolStatus(null);
    }
  }, [setSending, setStreaming, setToolStatus]);

  /**
   * Confirm an action (like saving a person)
   */
  const confirm = useCallback(
    async (preview: ActionPreview) => {
      if (!currentSessionUuid) return;

      try {
        const result = await confirmAction(
          currentSessionUuid,
          preview.tool,
          preview.arguments
        );

        setActionPreview(null);

        // Add confirmation message
        if (result.success) {
          const successMsg = result.result?.person
            ? `✅ ${result.result.person.name} je sačuvan/a kao ${result.result.person.type_label}.`
            : result.result?.message || t('errors.actionSuccess');

          addMessage({
            role: 'assistant',
            content: successMsg,
            created_at: new Date().toISOString(),
          });
        } else {
          addMessage({
            role: 'assistant',
            content: `❌ ${result.error || t('errors.actionFailed')}`,
            created_at: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error('Confirm action failed:', error);
        addMessage({
          role: 'assistant',
          content: `❌ ${t('errors.serverComm')}`,
          created_at: new Date().toISOString(),
        });
      }
    },
    [currentSessionUuid, addMessage, t]
  );

  /**
   * Cancel action preview
   */
  const cancelPreview = useCallback(() => {
    setActionPreview(null);
  }, []);

  /**
   * Submit feedback for a message
   */
  const onFeedback = useCallback(
    async (messageId: number, type: 'up' | 'down') => {
      if (!currentSessionUuid) return;

      // Optimistic update
      setFeedback((prev) => ({ ...prev, [messageId]: type }));

      try {
        await submitFeedback(messageId, currentSessionUuid, type);
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        // Revert on error
        setFeedback((prev) => {
          const next = { ...prev };
          delete next[messageId];
          return next;
        });
      }
    },
    [currentSessionUuid]
  );

  return {
    messages,
    isSending,
    isStreaming,
    toolStatus,
    actionPreview,
    feedback,
    send,
    abort,
    confirm,
    cancelPreview,
    onFeedback,
  };
}

export default useChat;
